import { Toaster } from "@/components/ui/toaster";
import { scrollWindowToTop } from "@/hooks/useScrollToTopOnChange";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { PostHogProvider } from "posthog-js/react";
import posthog from "posthog-js";
import { useEffect } from "react";
import { LanguageProvider } from "./contexts/LanguageContext";
import { RouteSeo } from "./components/Seo";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import {
  RouteRedirect,
  PropertiesLocationRedirect,
} from "./components/RouteRedirects";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import Navigation from "./components/Navigation";
import MarketingSurface from "./components/MarketingSurface";
import Footer from "./components/Footer";
import { useSiteFooterHidden } from "./hooks/useSiteFooter";
import { CookieConsent } from "./components/CookieConsent";
import Home from "./pages/Home";
import KeywordLanding from "./pages/KeywordLanding";
import { KEYWORD_PAGES } from "./lib/seo/config";
import Concept from "./pages/Concept";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import Community from "./pages/Community";
import Careers from "./pages/Careers";
import Contact from "./pages/Contact";
import BookingConfirmation from "./pages/BookingConfirmation";
import Owners from "./pages/Owners";
import Dashboard from "./pages/Dashboard";
import DashboardRoute from "./pages/DashboardRoute";
import Admin from "./pages/Admin";
import Operators from "./pages/Operators";
import OperatorConfirmed from "./pages/OperatorConfirmed";
import OwnerSignup from "./pages/OwnerSignup";
import OwnerVerify from "./pages/OwnerVerify";
import OwnerDocumentsPage from "./pages/admin/v2/OwnerDocumentsPage";
import OwnerPayoutsPage from "./pages/admin/v2/OwnerPayoutsPage";
import OwnerReferralsPage from "./pages/admin/v2/OwnerReferralsPage";
import OperatorBillingPage from "./pages/admin/OperatorBillingPage";
import { PaywallProvider } from "./components/paywall/PaywallProvider";
import { EntitlementRouteGate } from "./components/paywall/EntitlementRouteGate";
import { OperatorRouteGate } from "./components/admin/OperatorRouteGate";
import V2LessorsListPage from "./pages/admin/v2/LessorsListPage";
import V2DirectoryPage from "./pages/admin/v2/DirectoryPage";
import BrainPage from "./pages/admin/v2/BrainPage";
import { BrainRouteGate } from "./components/admin/brain/BrainRouteGate";
import V2PropertiesList from "./pages/admin/v2/PropertiesList";
import OwnerNotifications from "./pages/admin/v2/OwnerNotifications";
import V2PropertyNew from "./pages/admin/v2/PropertyNew";
import V2PropertyDetail from "./pages/admin/v2/PropertyDetail";
import V2ApartmentDetail from "./pages/admin/v2/ApartmentDetail";
import V2RoomDetail from "./pages/admin/v2/RoomDetail";
import V2TenantDetail from "./pages/admin/v2/TenantDetail";
import ReconciliationRouter from "./pages/admin/v2/ReconciliationRouter";
import OperatorsAdminPage from "./pages/admin/v2/OperatorsAdminPage";
import V2CreateTenantPage from "./pages/admin/v2/CreateTenant";
import V2TranslationsPage from "./pages/admin/v2/TranslationsPage";
import V2ProtocolDetailPage from "./pages/admin/v2/ProtocolDetailPage";
import { OwnerPropertyDetail } from "./components/owner/OwnerPropertyDetail";
import { OwnerRoomDetail } from "./components/owner/OwnerRoomDetail";
import { OwnerApartmentDetail } from "./components/owner/OwnerApartmentDetail";
import AdminAccountPage from "./pages/admin/AccountPage";
import { FeatureFlagGate } from "./components/admin/v2/FeatureFlagGate";
import DocuSignCallback from "./pages/DocuSignCallback";
import CheckEmail from "./pages/CheckEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { RecoveryLinkRedirect } from "./components/auth/RecoveryLinkRedirect";
import LegalPage from "./pages/LegalPage";
import NotFound from "./pages/NotFound";
import SentryTestPage from "./pages/SentryTestPage";
import Onboarding from "./pages/Onboarding";
import DepositRelease from "./pages/DepositRelease";
import { OwnerDemoCta } from "./components/owner/OwnerDemoCta";

const queryClient = new QueryClient();

/**
 * Hide the marketing nav + footer on full-screen flows like /onboarding/*.
 * The owner onboarding flow signs a chargeable contract; site navigation there
 * invites the owner to wander off mid-signature, and the flow carries its own
 * stage rail and back control.
 */
const FULLSCREEN_PREFIXES = ["/onboarding"];

function isFullscreenFlow(pathname: string) {
  return FULLSCREEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function ChromeShell() {
  const location = useLocation();
  if (isFullscreenFlow(location.pathname)) return null;
  return <Navigation />;
}

function FooterShell() {
  const location = useLocation();
  // A page that brings its own footer — the V2 tenant dashboard — says so
  // through this store. See useSiteFooter for why a path check cannot.
  if (useSiteFooterHidden()) return null;
  if (isFullscreenFlow(location.pathname)) return null;
  return <Footer />;
}

function PostHogPageviewTracker() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview");
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Reset scroll to the top of the page on every client-side navigation (COL-255).
 * Without this, React Router keeps the previous scroll offset, so following a
 * link/opening a new page lands the user mid-page instead of at the top.
 *
 * Keyed on the pathname and the `?tab=` param: switching a section tab is
 * "opening a new page" for the reader, but other query changes (filters, sort,
 * dialog flags) are in-page state and must not yank the scroll. Screens that
 * swap content with React state only use `useScrollToTopOnChange` themselves.
 */
function ScrollToTop() {
  const { pathname, search } = useLocation();
  const tab = new URLSearchParams(search).get("tab");
  useEffect(() => {
    scrollWindowToTop();
  }, [pathname, tab]);
  return null;
}

/** Old /protocol/:id and /protocol/:id/:step deep links → redirect into the dashboard tab. */
function ProtocolRedirect() {
  const { id, step } = useParams<{ id: string; step?: string }>();
  const stepKey =
    step === "photos" ||
    step === "inventory" ||
    step === "common" ||
    step === "sign" ||
    step === "done"
      ? step
      : "photos";
  return (
    <Navigate
      to={`/dashboard?tab=checkin&p=${id ?? ""}&step=${stepKey}`}
      replace
    />
  );
}

const App = () => (
  <PostHogProvider client={posthog}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <ScrollToTop />
          <MarketingSurface />
          <RecoveryLinkRedirect />
          <PostHogPageviewTracker />
          <LanguageProvider>
            <RouteSeo />
            <AuthProvider>
              <Toaster />
              <Sonner />
              <CookieConsent />
              <ChromeShell />
              <PaywallProvider>
                <Routes>
                  <Route path="/" element={<Home />} />
                  {/* Semi-programmatic keyword landing pages (COL-600). One
                      explicit route per KEYWORD_PAGES row — not a catch-all
                      /:slug, which would swallow unknown paths before
                      NotFound. prerender.ts bakes the same meta/JSON-LD. */}
                  {KEYWORD_PAGES.map((p) => (
                    <Route
                      key={p.path}
                      path={p.path}
                      element={<KeywordLanding slug={p.path} />}
                    />
                  ))}
                  <Route path="/concept" element={<Concept />} />
                  <Route path="/rooms" element={<Properties />} />
                  <Route path="/rooms/:location" element={<Properties />} />
                  <Route
                    path="/properties"
                    element={<RouteRedirect to="/rooms" />}
                  />
                  <Route
                    path="/properties/:location"
                    element={<PropertiesLocationRedirect />}
                  />
                  <Route path="/property/:id" element={<PropertyDetail />} />
                  <Route path="/community" element={<Community />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route
                    path="/booking-confirmation"
                    element={<BookingConfirmation />}
                  />
                  <Route path="/developers" element={<Owners />} />
                  {/* /owners used to serve the Developers page; the URL is
                      reassigned here to the Operators ("Owners") page per the
                      new site map, so it intentionally does not redirect to
                      /developers. */}
                  <Route path="/owners" element={<Operators />} />
                  {/* Free-tier owner signup. /r/:code is the referral link;
                      both render the same form. */}
                  <Route path="/owners/signup" element={<OwnerSignup />} />
                  <Route path="/r/:code" element={<OwnerSignup />} />
                  <Route path="/owners/verify" element={<OwnerVerify />} />
                  <Route
                    path="/owners/confirmed"
                    element={<OperatorConfirmed />}
                  />
                  <Route
                    path="/operators"
                    element={<RouteRedirect to="/owners" />}
                  />
                  <Route
                    path="/operators/confirmed"
                    element={<RouteRedirect to="/owners/confirmed" />}
                  />
                  <Route path="/check-email" element={<CheckEmail />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/onboarding/:token" element={<Onboarding />} />
                  {/* The lessor's representative releases a deposit here. Public
                      and unauthenticated: a lessor is a record with an email,
                      not necessarily a user account. The token is the
                      credential, validated in the edge function against a
                      stored hash. */}
                  <Route
                    path="/deposit-release/:token"
                    element={<DepositRelease />}
                  />
                  <Route path="/imprint" element={<LegalPage />} />
                  <Route path="/privacy-policy" element={<LegalPage />} />
                  <Route path="/terms-of-service" element={<LegalPage />} />
                  {/*
                    DashboardRoute, not Dashboard. It reads
                    isTenantDashboardV2Enabled() and renders V2 only when the
                    flag is on -- which it is NOT on main's production builds.
                    With the flag off it returns <Dashboard /> unchanged, so
                    this swap is a no-op for every tenant until someone sets
                    VITE_TENANT_DASHBOARD_V2 deliberately.
                  */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardRoute />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin>
                        <Admin />
                      </ProtectedRoute>
                    }
                  />
                  {/* V2 admin module — gated behind VITE_ADMIN_V2 flag.
                    Flag off → FeatureFlagGate redirects to /admin?tab=properties.
                    Phase B1 ships these as stub pages; bodies fill in B3–B7. */}
                  <Route
                    path="/admin/owners"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <V2LessorsListPage />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/lessors"
                    element={<RouteRedirect to="/admin/owners" />}
                  />
                  {/* Directory — the roles table and tier cards. Used to be a tab
                      on /admin/owners; `?tab=directory` redirects here. */}
                  <Route
                    path="/admin/directory"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <V2DirectoryPage />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  {/* colivIQ Brain (COL-527) — the admin to-do deck. Staff
                    only, behind VITE_ADMIN_BRAIN (see BrainRouteGate). */}
                  <Route
                    path="/admin/brain"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <BrainRouteGate>
                            <BrainPage />
                          </BrainRouteGate>
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/properties"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <V2PropertiesList />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/notifications"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <OwnerNotifications />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/documents"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate ownerSection>
                          <OwnerDocumentsPage />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/payouts"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate ownerSection>
                          <OwnerPayoutsPage />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/refer"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate ownerSection>
                          <OwnerReferralsPage />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/properties/new"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <EntitlementRouteGate trigger="create_property">
                            <V2PropertyNew />
                          </EntitlementRouteGate>
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/properties/:id"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <V2PropertyDetail />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/properties/:id/apartments/:aptId"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <V2ApartmentDetail />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/properties/:id/rooms/:roomId"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <V2RoomDetail />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/properties/:id/rooms/:roomId/tenants/:bookingId"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <V2TenantDetail />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/reconciliation"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          {/* Operators get the bank-connect flow; staff the
                              finAPI review page — see ReconciliationRouter. */}
                          <ReconciliationRouter />
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/operators"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          {/* CoLiviq-staff only: OperatorRouteGate bounces an
                              operator back to their own analytics. */}
                          <OperatorRouteGate>
                            <OperatorsAdminPage />
                          </OperatorRouteGate>
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tenants/new"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <EntitlementRouteGate trigger="create_tenant">
                            <V2CreateTenantPage />
                          </EntitlementRouteGate>
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/translations"
                    element={
                      <ProtectedRoute requireAdmin>
                        <FeatureFlagGate>
                          <OperatorRouteGate>
                            <V2TranslationsPage />
                          </OperatorRouteGate>
                        </FeatureFlagGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/billing"
                    element={
                      <ProtectedRoute requireAdmin>
                        <OperatorBillingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/protocols/:id"
                    element={
                      <ProtectedRoute requireAdmin>
                        <V2ProtocolDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Account & profile — the signed-in account's own details.
                      Not behind the V2 flag: it is the only place staff can edit
                      their own name, email and password, and the strip's Settings
                      entry is org-level (Roles & access). */}
                  <Route
                    path="/admin/account"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminAccountPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Owner portal — read-only per-property operational + financial
                      detail. Not behind the V2 flag; owners reach it from the
                      analytics dashboard. Ownership is enforced by RLS/RPC and the
                      useOwnerPropertyAccess guard inside the component. */}
                  <Route
                    path="/admin/owner/property/:id"
                    element={
                      <ProtectedRoute requireAdmin>
                        <OwnerPropertyDetail />
                      </ProtectedRoute>
                    }
                  />
                  {/* One room, read-only — what the property overview's room
                      link opens. Its own route rather than a dialog so a room
                      can be linked to and returned to. */}
                  <Route
                    path="/admin/owner/room/:roomId"
                    element={
                      <ProtectedRoute requireAdmin>
                        <OwnerRoomDetail />
                      </ProtectedRoute>
                    }
                  />
                  {/* One apartment, read-only — and where an apartment-level
                      document upload happens. */}
                  <Route
                    path="/admin/owner/apartment/:apartmentId"
                    element={
                      <ProtectedRoute requireAdmin>
                        <OwnerApartmentDetail />
                      </ProtectedRoute>
                    }
                  />
                  {/* Legacy deep-links from old emails → bounce into the dashboard tab. */}
                  <Route path="/protocol/:id" element={<ProtocolRedirect />} />
                  <Route
                    path="/protocol/:id/:step"
                    element={<ProtocolRedirect />}
                  />
                  <Route
                    path="/admin/docusign/callback"
                    element={<DocuSignCallback />}
                  />
                  {/* Sentry smoke test: set VITE_ENABLE_SENTRY_TEST=true, then visit /__debug/sentry-test */}
                  <Route
                    path="/__debug/sentry-test"
                    element={<SentryTestPage />}
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </PaywallProvider>
              <OwnerDemoCta />
              <FooterShell />
              <ImpersonationBanner />
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </PostHogProvider>
);

export default App;
